import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { cn } from '../lib/utils';
import Equipment3DCard from '../components/treasure/Equipment3DCard';
import {
  Search, Package, Coins, Weight, Zap, FlaskConical,
  Sword, Shield, Sparkles, Scroll, CircleDot, SlidersHorizontal, Crown
} from 'lucide-react';

const API_URL = '/api';

const EQUIPMENT_TYPES = ['weapon', 'armor', 'potion', 'scroll', 'wondrous', 'ring', 'wand', 'staff', 'ammunition', 'tool', 'material', 'quest_item'];
const EQUIPMENT_RARITIES = ['common', 'uncommon', 'rare', 'very_rare', 'legendary', 'artifact'];

const RARITY_COLORS = {
  common: 'bg-gray-500', uncommon: 'bg-green-500', rare: 'bg-blue-500',
  very_rare: 'bg-purple-500', legendary: 'bg-amber-500', artifact: 'bg-red-600'
};

const TYPE_ICONS = {
  weapon: Sword, armor: Shield, potion: FlaskConical, scroll: Scroll,
  wondrous: Sparkles, ring: CircleDot, wand: Zap, staff: Zap,
  ammunition: CircleDot, tool: SlidersHorizontal, material: Package, quest_item: Crown
};

function InventoryDetailDialog({ open, onOpenChange, item, language }) {
  if (!item) return null;
  const TypeIcon = TYPE_ICONS[item.type] || Sparkles;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-cinzel flex items-center gap-2">
            <TypeIcon className="h-5 w-5" />
            {item.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Image */}
          {item.image_url && (
            <div className="rounded-lg overflow-hidden h-48 bg-muted">
              <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={cn("text-xs text-white", RARITY_COLORS[item.rarity])}>
              {t(`rarity_${item.rarity}`, language)}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {t(`equipment_type_${item.type}`, language)}
            </Badge>
            {item.slot && item.slot !== 'none' && (
              <Badge variant="outline" className="text-xs">
                {t(`slot_${item.slot}`, language)}
              </Badge>
            )}
          </div>

          {/* Description */}
          {item.description && (
            <p className="text-sm text-muted-foreground">{item.description}</p>
          )}

          {/* Effect */}
          {item.effect && (
            <div className="rounded-lg bg-primary/5 p-3">
              <p className="text-sm font-medium">{item.effect}</p>
            </div>
          )}

          {/* Stats */}
          {item.stats && (
            <p className="text-sm"><strong>{t('equipment_stats', language)}:</strong> {item.stats}</p>
          )}

          {/* Numeric info */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {item.value_gp > 0 && (
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-amber-500" />
                <span>{item.value_gp} gp</span>
              </div>
            )}
            {item.weight > 0 && (
              <div className="flex items-center gap-2">
                <Weight className="h-4 w-4 text-muted-foreground" />
                <span>{item.weight} lb</span>
              </div>
            )}
            {item.bonus && (
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>{item.bonus}</span>
              </div>
            )}
            {item.charges != null && (
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-purple-500" />
                <span>{item.charges}/{item.max_charges || '?'}</span>
              </div>
            )}
          </div>

          {/* Requirements */}
          {item.requirements && (
            <p className="text-xs text-muted-foreground italic">
              {t('equipment_requirements', language)}: {item.requirements}
            </p>
          )}

          {/* Tags */}
          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.tags.map((tag, i) => (
                <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PlayerInventoryPage() {
  const { campaignId } = useParams();
  const { language } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [rarityFilter, setRarityFilter] = useState('all');
  const [detailItem, setDetailItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.type = typeFilter;
      if (rarityFilter !== 'all') params.rarity = rarityFilter;
      if (search.trim()) params.search = search.trim();
      const res = await axios.get(`${API_URL}/campaigns/${campaignId}/inventory`, { params });
      setItems(res.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [campaignId, typeFilter, rarityFilter, search]);

  useEffect(() => {
    if (campaignId) fetchInventory();
  }, [campaignId, fetchInventory]);

  const totalWeight = items.reduce((sum, it) => sum + (it.weight || 0), 0);
  const totalValue = items.reduce((sum, it) => sum + (it.value_gp || 0), 0);

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-cinzel text-3xl font-bold">{t('inventory_title', language)}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {items.length} {t('inventory_item_count', language)}
              {totalWeight > 0 && <span className="ml-3">{totalWeight} lb</span>}
              {totalValue > 0 && <span className="ml-3 text-amber-500">{totalValue} gp</span>}
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t('search', language)}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-[140px]"><SelectValue placeholder={t('filter_all_types', language)} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filter_all_types', language)}</SelectItem>
                {EQUIPMENT_TYPES.map(tp => (
                  <SelectItem key={tp} value={tp}>{t(`equipment_type_${tp}`, language)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={rarityFilter} onValueChange={setRarityFilter}>
              <SelectTrigger className="w-full sm:w-[140px]"><SelectValue placeholder={t('filter_all_rarities', language)} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filter_all_rarities', language)}</SelectItem>
                {EQUIPMENT_RARITIES.map(r => (
                  <SelectItem key={r} value={r}>
                    <span className="flex items-center gap-2">
                      <div className={cn("h-2 w-2 rounded-full", RARITY_COLORS[r])} />
                      {t(`rarity_${r}`, language)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="rounded-xl border p-6 animate-pulse space-y-4">
                <div className="h-32 bg-muted rounded" />
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center">
            <Package className="h-20 w-20 mx-auto text-muted-foreground/30 mb-6" />
            <h3 className="font-cinzel text-xl font-bold mb-2">{t('inventory_empty', language)}</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {t('inventory_empty_desc', language)}
            </p>
          </div>
        ) : (
          <div className="equipment-cards-grid">
            {items.map(item => (
              <Equipment3DCard
                key={item.id}
                item={item}
                language={language}
                readOnly
                onEdit={(eq) => { setDetailItem(eq); setDetailOpen(true); }}
              />
            ))}
          </div>
        )}

        {/* Detail dialog */}
        <InventoryDetailDialog
          open={detailOpen}
          onOpenChange={setDetailOpen}
          item={detailItem}
          language={language}
        />
      </div>
    </MainLayout>
  );
}
